import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@core/services/api/apiRequest";
import type { CircleListResponse } from "../circle.types";
import { CIRCLES_API } from "../circle.constants";

/**
 * Arguments for useFetchUserCircles hook.
 */
export type UseFetchUserCirclesArgs = {
  /** Whether the query is enabled */
  isEnabled?: boolean;
  /** Whether the store has been hydrated from localStorage */
  isHydrated?: boolean;
  /** Refetch on window focus */
  refetchOnWindowFocus?: boolean;
  /** Refetch on reconnect */
  refetchOnReconnect?: boolean;
  /** Current user (to determine if logged in) */
  user: { id: number } | null;
  /** Optional program ID to filter circles by */
  programId?: number;
};

/**
 * Hook to fetch user circles from the API.
 * Returns React Query result directly - this is the source of truth for circle data.
 *
 * @example
 * ```typescript
 * // In a component
 * const { data, isLoading, isFetching } = useFetchUserCircles({
 *   user,
 * });
 *
 * if (isLoading) return <Loader />;
 *
 * return (
 *   <CircleList
 *     circles={data?.circles ?? []}
 *   />
 * );
 * ```
 */
export function useFetchUserCircles({
  isEnabled = true,
  isHydrated = true,
  refetchOnWindowFocus = true,
  refetchOnReconnect = true,
  user,
  programId,
}: UseFetchUserCirclesArgs) {
  const queryParams = programId ? `?program_id=${programId}` : "";

  return useQuery<CircleListResponse, Error>({
    queryKey: ["user-circles", programId],
    queryFn: async () => {
      return apiRequest(
        `${CIRCLES_API}${queryParams}`,
        "GET"
      ) as Promise<CircleListResponse>;
    },
    enabled: isEnabled && isHydrated && !!user,
    refetchOnWindowFocus,
    refetchOnReconnect,
  });
}

export default useFetchUserCircles;

